import { centsToDollars, parsePrice } from "helpers/price";
import {
  IOrderBalanceBox,
  IOrderBalanceBoxRow,
  IOrderBalanceEvent,
  IOrderBalanceParty,
} from "types/OrderBalance";

export const formatCents = (cents: number | null | undefined): string =>
  cents === null || cents === undefined
    ? ""
    : parsePrice(centsToDollars(cents));

export const formatDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: "UTC" }) : "";

export type EventTableRow = Record<string, string | number | boolean | null>;

export const partyField = (
  partyId: string,
  field: "total" | "provider" | "fee"
): string => `party_${partyId}_${field}`;

/**
 * Flattens ledger events into DataTable rows: one row per event, three columns per provider
 * (Total / Provider / Clear Fee), exactly like the workbook's Order Overview and Payment Activity.
 */
export const buildEventRows = (
  events: IOrderBalanceEvent[],
  providers: IOrderBalanceParty[]
): EventTableRow[] =>
  events.map((event) => {
    const row: EventTableRow = {
      id: event.id,
      date: event.date,
      type: event.type,
      label: event.label,
      patient: event.patientInCents,
      clearDirect: event.clearDirectInCents,
      unallocated: event.unallocatedInCents,
      verified: event.verified,
      notes: event.notes.join(" "),
    };
    providers.forEach((provider) => {
      const amounts = event.byParty[provider.id];
      row[partyField(provider.id, "total")] = amounts?.totalInCents ?? null;
      row[partyField(provider.id, "provider")] =
        amounts?.providerInCents ?? null;
      row[partyField(provider.id, "fee")] = amounts?.clearFeeInCents ?? null;
    });
    return row;
  });

export const sumField = (rows: EventTableRow[], field: string): number =>
  rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

export const REPORTED_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedPaidInCents",
  "reportedTotalInCents",
  "reportedCashBalanceInCents",
  "reportedTppBalanceInCents",
];

// Clear Health has no Total / TPP columns in the workbook: those cells stay blank, never "pending".
const CLEAR_BLANK_FIELDS: (keyof IOrderBalanceBoxRow)[] = [
  "reportedTotalInCents",
  "reportedTppBalanceInCents",
];

/**
 * True when a balance-box cell is a reported figure that no weekly report covers yet — rendered as a
 * muted "pending" marker rather than $0.00, which would read as a discrepancy.
 */
export const isReportedPending = (
  balanceBox: IOrderBalanceBox,
  row: IOrderBalanceBoxRow,
  field: keyof IOrderBalanceBoxRow
): boolean =>
  !balanceBox.reportsCoverOrder &&
  REPORTED_FIELDS.includes(field) &&
  row[field] === null &&
  !(row.kind === "CLEAR_HEALTH" && CLEAR_BLANK_FIELDS.includes(field));

// The table renders "<type> — <label>"; a label that already names the type is shown on its own.
export const eventTypeText = (type: string, label: string): string => {
  if (!label || label === type) return type;
  return label.startsWith(`${type} — `) ? label : `${type} — ${label}`;
};
